import React, { useEffect, useState } from 'react';
import {
  PayPalButtons,
  PayPalScriptProvider,
  FUNDING,
} from '@paypal/react-paypal-js';
import { useAuth } from '../auth/auth-hook';
import { openDialogAlert } from '../alert/alert-actions';
import {
  AddUserBroadcastDataInput,
  Plan,
  StripeCoupon,
  StudentCredentialInput,
  Subscription,
  SubscriptionBillingInput,
  useAddUserPreferencesDataMutation,
  useCreatePaypalSubscriptionMutation,
  useCreateStudentCredentialMutation,
} from '../../shared/types/generated';
import { Box, Typography } from '@mui/material';
import { OnApproveData } from '@paypal/paypal-js/types/components/buttons';
import { getPostUrlWithoutQuery } from '../auth/auth-utils';
import { PAYPAL_CLIENT_ID } from '../../shared/constans';
import { clientUpload } from '../../shared/apollo';
import { Interests } from '../payment/payment-types';
import { translateInterests } from '../payment/payment-utils';
import { triggerEvent } from '../remote-education/remote-education-utils';
import { getRedirectsOnPurchaseSub } from '../../shared/utils';

type PaypalProps = {
  subscriptionBilling: SubscriptionBillingInput | null;
  coupon?: StripeCoupon | null;
  activeSubscription?: Subscription;
  studentCredential: StudentCredentialInput;
  plan?: Plan;
  interests?: Interests[];
  broadcastData?: AddUserBroadcastDataInput;
  studentTerms?: boolean;
};

const PaypalButton: React.FC<PaypalProps> = ({
  subscriptionBilling,
  coupon,
  activeSubscription,
  studentCredential,
  plan,
  interests,
  broadcastData,
  studentTerms,
}) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const [billingDisabled, setBillingDisabled] = useState(true);

  const [callSubscriptionAction] = useCreatePaypalSubscriptionMutation({
    onError: (error) => {
      openDialogAlert(error.message);
    },
  });

  const [updateUserPreferences] = useAddUserPreferencesDataMutation({
    onCompleted: (data) => {
      console.log({ data });
    },
    onError: () => {
      openDialogAlert('Hubo un error al procesar las preferencias del usuario');
      setLoading(false);
    },
  });

  const [callCreateStudentCredential] = useCreateStudentCredentialMutation({
    client: clientUpload,
    onCompleted: () => {
      // TODO: CAMBIAR URL DE VERIFICACIÓN
      setLoading(false);
      window.location.href = getRedirectsOnPurchaseSub(plan as Plan);
    },
    onError: (error) => {
      console.error(error);
      setLoading(false);
      openDialogAlert('Error al guardar la credencial estudiantil');
    },
  });

  useEffect(() => {
    const subscriptionBillingChecked =
      subscriptionBilling &&
      Object.keys(subscriptionBilling).find(
        (key) =>
          !subscriptionBilling[key as keyof SubscriptionBillingInput] &&
          key !== 'phone1' &&
          key !== 'address1' &&
          key !== 'internal_number' &&
          key !== 'country' &&
          key !== 'aditional_name',
      );

    if (
      plan?.subscription_billing_is_required &&
      subscriptionBillingChecked &&
      (!subscriptionBilling?.address ||
        !subscriptionBilling?.city ||
        !subscriptionBilling?.external_number ||
        !subscriptionBilling?.phone ||
        !subscriptionBilling?.postal_code ||
        !subscriptionBilling?.state)
    ) {
      setBillingDisabled(true);
      return;
    }
    if (!subscriptionBilling?.phone || !subscriptionBilling?.state) {
      setBillingDisabled(true);
      return;
    }

    if (!interests?.length) {
      setBillingDisabled(true);
      return;
    }

    if (plan?.subscription_student_credential_is_required) {
      const studentCredentialChecked: boolean =
        studentCredential?.file === null;

      if (studentCredentialChecked) {
        setBillingDisabled(true);
        return;
      }
    }

    if (!studentTerms) {
      setBillingDisabled(true);
      return;
    }

    setBillingDisabled(false);
  }, [
    subscriptionBilling,
    studentCredential,
    plan,
    user,
    interests?.length,
    studentTerms,
  ]);

  const paypalButtonKey = `${plan?.paypal?.paypal_id}-${coupon?.paypal_coupon_plan}-${billingDisabled}`;

  const handleSubscription = async (data: OnApproveData): Promise<void> => {
    const { subscriptionID, orderID } = data;
    callSubscriptionAction({
      variables: {
        data: {
          planId: plan?.id,
          subscriptionId: subscriptionID,
          orderId: orderID,
          subscriptionUrl: getPostUrlWithoutQuery(),
          billingInfo: subscriptionBilling,
          coupon: coupon?.coupon ?? undefined,
        },
      },
      onCompleted() {
        if (plan?.subscription_student_credential_is_required) {
          callCreateStudentCredential({
            variables: {
              data: {
                file: studentCredential.file,
              },
            },
          });
        } else {
          setLoading(false);
          window.location.href = getRedirectsOnPurchaseSub(plan as Plan);
        }
      },
    });
  };
  /**
   * console.log(
    'paypal:',
    billingDisabled || !!activeSubscription,
    { billingDisabled },
    !!activeSubscription,
  );
   */
  return plan && plan.paypal ? (
    <>
      <>
        {loading ? (
          <Box
            position={'fixed'}
            zIndex={99999999}
            top={0}
            left={0}
            width={'100%'}
            height={'100%'}
            component={'div'}
            sx={{ '-webkit-backdrop-filter': 'blur(10px) brightness(0.8)' }}
            style={{
              backdropFilter: 'blur(10px) brightness(0.8)',
              background: 'rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              height={'100%'}
            >
              <Typography
                fontFamily={'manop'}
                color={'white'}
                fontSize={'32px'}
              >
                Procesando, por favor espere…
              </Typography>
            </Box>
          </Box>
        ) : null}
      </>
      <PayPalButtons
        key={paypalButtonKey}
        fundingSource={FUNDING.PAYPAL}
        disabled={billingDisabled || !!activeSubscription}
        onClick={() => {
          setLoading(true);
          if (plan.title_front?.toLowerCase().includes('estándar')) {
            triggerEvent('btn_mensual_pp');
          } else if (plan.title_front?.toLowerCase()?.includes('profesional')) {
            triggerEvent('btn_anual_pp');
          } else {
            triggerEvent('btn-est-pp');
          }
          if (
            plan?.subscription_student_credential_is_required &&
            billingDisabled
          ) {
            openDialogAlert(
              'Los datos de la credencial estudiantil son obligatorios',
            );
          }
        }}
        createSubscription={async (data, actions) => {
          return actions.subscription
            .create({
              plan_id:
                coupon?.paypal_coupon_plan && coupon.paypal_coupon_plan !== ''
                  ? coupon?.paypal_coupon_plan
                  : (plan?.paypal?.paypal_id as string),
              subscriber: {
                email_address: user?.email,
              },
              custom_id: user?.email,
            })

            .then((orderId: string) => {
              return orderId;
            });
        }}
        style={{ label: 'subscribe' }}
        onApprove={async (data) => {
          setLoading(true);

          await handleSubscription(data);

          await updateUserPreferences({
            variables: {
              data: {
                user_id: user?.id ?? 0,
                must_send: broadcastData?.must_send,
                phone: broadcastData?.phone,
                interests: translateInterests(interests ?? []),
              },
            },
          });
        }}
        onError={(err) => {
          console.log({ err });
          return openDialogAlert(
            'Tenemos problemas para realizar la suscripción',
          );
        }}
      />
    </>
  ) : null;
};

export const PaypalWithoutStore: React.FC<PaypalProps> = ({
  subscriptionBilling,
  coupon,
  activeSubscription,
  studentCredential,
  plan,
  interests,
  broadcastData,
  studentTerms,
}) => {
  if (!plan?.paypal)
    return (
      <Typography variant="subtitle1" fontWeight={800} textAlign="center">
        Paypal no esta configurado para este plan, contacte a su administrador
      </Typography>
    );
  return (
    <PayPalScriptProvider
      options={{
        clientId: PAYPAL_CLIENT_ID,
        components: 'buttons',
        intent: 'subscription',
        vault: true,
      }}
    >
      <PaypalButton
        plan={plan}
        subscriptionBilling={subscriptionBilling}
        coupon={coupon}
        interests={interests}
        activeSubscription={activeSubscription}
        studentCredential={studentCredential}
        broadcastData={broadcastData}
        studentTerms={studentTerms}
      />
    </PayPalScriptProvider>
  );
};
